// The Search pane's record half: one `contains` query per map-review ledger
// table, each against the ledgers' virtual `search` property, run in parallel
// and normalised into groups by `searchResults.ts`. The address half lives in
// the shared `useAddressSearch`; this composable never calls it and never
// writes the store.
//
// Two rules make the list honest. A table whose query fails becomes a named
// failed group rather than a missing one, so the operator sees which table could
// not be searched instead of silently losing its matches. And a stale response —
// the id of a round older than the latest keystroke — is dropped whole, because
// a slower table's answer must never appear beside a newer term's.
import { ref, watch, type Ref } from "vue";

import { apiClient } from "@/client";
import {
	MAP_REVIEW_TABLES,
	SEARCH_PROPERTY,
} from "@/components/map-review/columns";
import { LEDGER_QUERY_PATH } from "@/components/map-review/ledgerQuery";
import type { SearchGroup } from "@/components/search/types";
import type {
	MapReviewQueryBody,
	MapReviewQueryEnvelope,
	MapReviewRow,
	MapReviewTable,
} from "@/type/api";

import {
	RECORD_SEARCH_LIMIT,
	failedRecordGroup,
	recordGroup,
	type RecordSearchResult,
} from "./searchResults";

/** One table's group from the one generic QUERY the ledger already answers:
 * the term as a `contains` over the virtual `search` property, five rows. The
 * envelope's `filtered_total` is the group's total, so the list can say how many
 * matches the page did not show. */
async function fetchRecordGroup(
	table: MapReviewTable,
	term: string,
): Promise<SearchGroup<RecordSearchResult>> {
	const response = await apiClient.JSONQuery<
		MapReviewQueryEnvelope<MapReviewRow>
	>(LEDGER_QUERY_PATH[table], {
		limit: RECORD_SEARCH_LIMIT,
		filter: [{ property: SEARCH_PROPERTY, operator: "contains", value: term }],
	} satisfies MapReviewQueryBody);
	return recordGroup(table, response);
}

/**
 * useMapReviewSearch watches an already-debounced term and resolves the nine
 * ledger tables' matches. `error` is set only when the whole round failed — the
 * server or the network is down, every table at once — because a single table's
 * failure is already visible as that table's failed group. `loading` is the
 * pane's own indicator; neither value is ever written back to the store.
 */
export function useMapReviewSearch(term: Ref<string>): {
	groups: Ref<SearchGroup<RecordSearchResult>[]>;
	loading: Ref<boolean>;
	error: Ref<string | null>;
} {
	const groups = ref<SearchGroup<RecordSearchResult>[]>([]);
	const loading = ref(false);
	const error = ref<string | null>(null);

	// The round's monotonic id: a keystroke can outrun nine table queries, so a
	// response that is no longer the latest is dropped rather than shown beside
	// a newer term's.
	let seq = 0;

	async function run(value: string): Promise<void> {
		const id = ++seq;
		if (!value) {
			groups.value = [];
			loading.value = false;
			error.value = null;
			return;
		}
		loading.value = true;
		error.value = null;
		const settled = await Promise.allSettled(
			MAP_REVIEW_TABLES.map((table) => fetchRecordGroup(table, value)),
		);
		if (id !== seq) return;
		const round = settled.map((entry, index) => {
			const table = MAP_REVIEW_TABLES[index];
			return entry.status === "fulfilled"
				? entry.value
				: failedRecordGroup(table);
		});
		// Only tables with matches, plus the failed ones, are groups the list
		// shows: an all-empty group's label over nothing is noise.
		groups.value = round.filter(
			(group) => group.results.length > 0 || group.failed,
		);
		loading.value = false;
		const failures = settled.filter(
			(entry): entry is PromiseRejectedResult => entry.status === "rejected",
		);
		if (failures.length === settled.length) {
			const reason = failures[0]?.reason;
			error.value =
				reason instanceof Error && reason.message
					? reason.message
					: "Failed to search records.";
		} else {
			error.value = null;
		}
	}

	watch(term, (value) => void run(value), { immediate: true });

	return { groups, loading, error };
}
